import { useState, useEffect } from 'react';
import { supabase, ContentResponse, Post, defaultPosts, Feature, defaultFeatures } from '../lib/supabase';

/**
 * Cache management utilities
 */
const getCacheKey = (tableName: string) => `supabase_${tableName}`;
const getCachedData = <T>(tableName: string): T | null => {
  try {
    const cached = localStorage.getItem(getCacheKey(tableName));
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

const setCachedData = <T>(tableName: string, data: T) => {
  try {
    localStorage.setItem(getCacheKey(tableName), JSON.stringify(data));
  } catch (err) {
    console.warn('Failed to cache data:', err);
  }
};

/**
 * Custom hook for fetching content from Supabase with cache-first approach
 * @param tableName - The Supabase table name to fetch from
 * @param fallbackData - Default data to use if no cache and fetch fails
 * @param enableRealtime - Whether to enable real-time subscriptions
 */
export function useSupabaseContent<T>(
  tableName: string,
  fallbackData: T,
  enableRealtime: boolean = true
): ContentResponse<T> {
  // Get cached data immediately
  const cachedData = getCachedData<T>(tableName);
  
  // If we have cached data, use it immediately. If not, start with null and show loader
  const [data, setData] = useState<T | null>(cachedData);
  const [loading, setLoading] = useState(!cachedData); // Show loading if no cache
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let subscription: any = null;

    const fetchContent = async () => {
      try {
        // If we have cached data, don't show loading state
        if (cachedData) {
          setLoading(false);
        }

        const { data: fetchedData, error: fetchError } = await supabase
          .from(tableName)
          .select('*')
          .single();

        if (fetchError) {
          console.warn(`Supabase fetch error for ${tableName}:`, fetchError);
          // Use fallback only if no cached data exists
          if (!cachedData) {
            setData(fallbackData);
            setError('Using default content');
          }
        } else {
          // Check if data has actually changed
          const newDataString = JSON.stringify(fetchedData);
          const currentDataString = JSON.stringify(data);
          
          if (newDataString !== currentDataString) {
            console.log(`Content updated for ${tableName}`);
            setData(fetchedData as T);
            setCachedData(tableName, fetchedData);
            setError(null);
          }
        }
      } catch (err) {
        console.warn(`Error fetching ${tableName}:`, err);
        // Use fallback only if no cached data exists
        if (!cachedData) {
          setData(fallbackData);
          setError('Using default content');
        }
      } finally {
        setLoading(false);
      }
    };

    // Fetch content (silently if we have cache)
    fetchContent();

    // Set up real-time subscription if enabled
    if (enableRealtime) {
      subscription = supabase
        .channel(`${tableName}_changes`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: tableName,
          },
          (payload) => {
            console.log(`Real-time update for ${tableName}:`, payload);
            if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
              const newData = payload.new as T;
              setData(newData);
              setCachedData(tableName, newData);
              setError(null);
            }
          }
        )
        .subscribe();
    }

    // Cleanup subscription on unmount
    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [tableName, fallbackData, enableRealtime, cachedData, data]);

  return { data, loading, error };
}

/**
 * Custom hook for fetching multiple items from Supabase with cache-first approach
 * @param tableName - The Supabase table name to fetch from
 * @param fallbackData - Default data to use if no cache and fetch fails
 * @param enableRealtime - Whether to enable real-time subscriptions
 * @param orderBy - Column to order by (optional)
 */
export function useSupabaseList<T>(
  tableName: string,
  fallbackData: T[],
  enableRealtime: boolean = true,
  orderBy?: { column: string; ascending?: boolean }
): ContentResponse<T[]> {
  // Get cached data immediately
  const cachedData = getCachedData<T[]>(tableName);
  
  // If we have cached data, use it immediately. If not, start with null and show loader
  const [data, setData] = useState<T[] | null>(cachedData);
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let subscription: any = null;

    const fetchContent = async () => {
      try {
        console.log(`useSupabaseList fetchContent for ${tableName}:`);
        console.log('- cachedData:', cachedData);
        console.log('- fallbackData:', fallbackData);
        
        if (cachedData) {
          setLoading(false);
        }

        let query = supabase.from(tableName).select('*');
        
        if (orderBy) {
          query = query.order(orderBy.column, { ascending: orderBy.ascending ?? true });
        }

        console.log('- About to execute query...');
        const { data: fetchedData, error: fetchError } = await query;
        console.log('- fetchedData:', fetchedData);
        console.log('- fetchError:', fetchError);

        if (fetchError) {
          console.warn(`Supabase fetch error for ${tableName}:`, fetchError);
          if (!cachedData) {
            setData(fallbackData);
            setError('Using default content');
          }
        } else {
          // If we got empty results and no cached data, use fallback
          if ((!fetchedData || fetchedData.length === 0) && !cachedData) {
            console.log(`Empty results for ${tableName}, using fallback data`);
            setData(fallbackData);
            setError('Using default content');
          } else {
            const newDataString = JSON.stringify(fetchedData);
            const currentDataString = JSON.stringify(data);
            
            if (newDataString !== currentDataString) {
              console.log(`Content updated for ${tableName}`);
              setData(fetchedData as T[]);
              setCachedData(tableName, fetchedData);
              setError(null);
            }
          }
        }
      } catch (err) {
        console.warn(`Error fetching ${tableName}:`, err);
        if (!cachedData) {
          setData(fallbackData);
          setError('Using default content');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchContent();

    if (enableRealtime) {
      subscription = supabase
        .channel(`${tableName}_changes`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: tableName,
          },
          (payload) => {
            console.log(`Real-time update for ${tableName}:`, payload);
            // Refetch all data for list changes to maintain proper ordering
            fetchContent();
          }
        )
        .subscribe();
    }

    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [tableName, fallbackData, enableRealtime, orderBy, cachedData, data]);

  return { data, loading, error };
}

/**
 * Specialized hook for About page content
 */
export function useAboutContent() {
  return useSupabaseContent('about_content', null, true);
}

/**
 * Specialized hook for Posts content
 */
// Accepts optional category filter
export function usePosts(category?: string) {
  // If category is provided, filter fallback as well
  const filteredFallback = category
    ? defaultPosts.filter((p) => p.category === category)
    : defaultPosts;

  // Use the hook as before - order by created_at descending (most recent first)
  const response = useSupabaseList<Post>(
    'posts',
    filteredFallback,
    true,
    { column: 'created_at', ascending: false }
  );

  // If category is provided, filter the data
  const filteredData = category && response.data
    ? response.data.filter((p) => p.category === category)
    : response.data;

  return { ...response, data: filteredData };
}

/**
 * Specialized hook for Features content
 */
export function useFeatures(category?: string) {

  // If category is provided, filter fallback as well
  const filteredFallback = category
    ? defaultFeatures.filter((f) => f.category === category)
    : defaultFeatures;

  console.log('useFeatures Debug:');
  console.log('- category:', category);
  console.log('- filteredFallback:', filteredFallback);
  console.log('- filteredFallback count:', filteredFallback.length);

  // Use the hook as before
  const response = useSupabaseList<Feature>(
    'features',
    filteredFallback,
    true,
    { column: 'order_index', ascending: true }
  );

  console.log('- useSupabaseList response:', response);

  // If category is provided, filter the data
  const filteredData = category && response.data
    ? response.data.filter((f) => f.category === category)
    : response.data;

  console.log('- final filteredData:', filteredData);

  return { ...response, data: filteredData };
}